"use client";

import Link from "next/link";
import {ContactsWorkspace} from "@/components/contacts-workspace";
import {UserSettingsDialog} from "@/components/user-settings-dialog";
import {MailNotificationCenter} from "@/components/mail-notification-center";
import type {MailNotificationTarget} from "@/lib/user/mail-notifications";
import {contactCopy,type ContactListSnapshot} from "@/lib/user/contacts";
import {useMailUnread} from "@/lib/ui/use-mail-unread";
import {unreadLabel} from "@/lib/user/mail-unread";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { ConversationHistoryMenu } from "@/components/conversation-history-menu";
import {GoogleMapsResultPresentation, googleMapsResultSummary, isGoogleMapsPresentation} from "@/components/google-maps-result";
import { NewConversationChooser } from "@/components/new-conversation-chooser";
import {
  useDesignWorkflowConversation,
  visibleWorkflowMessages,
} from "@/lib/workflows/design-workflow-conversation";
import type { MessageReceipt } from "@/lib/user/conversation-orchestration";
import type { UserWorkflowEntry } from "@/lib/workflows/user-entries";
import { LanguageSwitcher, useI18n } from "@/lib/i18n/provider";
import { useChatScrollFollow } from "@/lib/ui/use-chat-scroll-follow";
import { conversationSelection } from "@/lib/user/conversation-selection";
import type {UserCreditBalance} from "@/lib/user/credits";

type DesignSurfaceProps = {
  guest?: boolean;
  compatibilityText?: string;
  accountEmail?: string;
  initialWorkflowEntries?: UserWorkflowEntry[];
  initialCreditBalance?: UserCreditBalance;
};

type OptimisticComposerMessage = {
  id: string;
  text: string;
  phase: "processing" | "failed";
  operation: "start" | "send";
  workflowKey: string;
  idempotencyKey: string;
  baselineSequence: number;
  threadID?: string;
};

type IconName =
  | "chat"
  | "inbox"
  | "star"
  | "settings"
  | "bot"
  | "user"
  | "paperclip"
  | "send"
  | "chevron";

function Icon({
  name,
  className = "",
}: {
  name: IconName;
  className?: string;
}) {
  return (
    <svg className={`design-icon ${className}`} aria-hidden="true">
      <use href={`#design-i-${name}`} />
    </svg>
  );
}

/**
 * 前台唯一工作台外壳：展示动态工作流入口，并在同一视觉上下文内恢复、发送和确认 Conversation。
 * 输入：可信 SSR 账号资料与可见工作流目录；输出：保持首页主题不变的选择态或工作流对话态。
 */
export function SoftmapDesignSurface({
  guest = false,
  compatibilityText = "",
  accountEmail: initialAccountEmail = "",
  initialWorkflowEntries,
  initialCreditBalance,
}: DesignSurfaceProps) {
  const [active, setActive] = useState(() => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("outreach_return") === "contacts" ? "contacts" : "chat");
  const [notice, setNotice] = useState("");
  const [isGuest, setIsGuest] = useState(guest);
  const [accountEmail, setAccountEmail] = useState(initialAccountEmail);
  // 未读缓存与联系人工作区都按账号隔离，切换身份不能保留上一账号的徽标或选择。
  const[mailTarget,setMailTarget]=useState<{account:string;target:MailNotificationTarget}>();
  const [contactsLoadedFor,setContactsLoadedFor]=useState<string>();
  const[contactSnapshot,setContactSnapshot]=useState<{account:string;value:ContactListSnapshot}>();
  // 只保留当前工作台账号的轻量列表，切回页面先展示；不写 localStorage 或跨账号复用。
  const cacheContacts=useCallback((value:ContactListSnapshot)=>setContactSnapshot({account:accountEmail,value}),[accountEmail]);
  const onContactsLoaded=useCallback(()=>setContactsLoadedFor(accountEmail),[accountEmail]);
  const mailUnread=useMailUnread(accountEmail,!isGuest,active!=="contacts"||contactsLoadedFor===accountEmail);
  const [creditBalance, setCreditBalance] = useState(initialCreditBalance);
  const [creditState, setCreditState] = useState<"guest" | "loading" | "ready" | "error">(
    guest ? "guest" : initialCreditBalance ? "ready" : "loading",
  );
  const [loginOpen, setLoginOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "register" | "recovery">("login");
  const [loginError, setLoginError] = useState("");
  const [loginPending, setLoginPending] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || new URLSearchParams(window.location.search).get("outreach_settings") !== "1") return;
    const timer = window.setTimeout(() => {
      if (new URLSearchParams(window.location.search).get("outreach_return") === "contacts") sessionStorage.setItem("outreach:oauth-return", "contacts");
      setSettingsOpen(true);
      const url = new URL(window.location.href);
      url.searchParams.delete("outreach_settings"); url.searchParams.delete("outreach_connection"); url.searchParams.delete("outreach_return");
      window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationExhausted, setVerificationExhausted] = useState(false);
  // 新密码只保存在当前组件内存；验证码成功、显式关闭或卸载都会立即覆盖引用。
  const registrationPasswordRef = useRef("");
  const registrationFailureCountRef = useRef(0);
  // 首页默认就是新会话意图选择器，避免首次进入时误显示演示性质的历史结果。
  const [newConversationMode, setNewConversationMode] = useState(true);
  const [selectedIntent, setSelectedIntent] = useState<string | null>(null);
  const [workflowEntries, setWorkflowEntries] = useState<UserWorkflowEntry[]>(
    initialWorkflowEntries ?? [],
  );
  const [workflowEntryState, setWorkflowEntryState] = useState<
    "loading" | "ready" | "empty" | "error"
  >(
    initialWorkflowEntries
      ? initialWorkflowEntries.length
        ? "ready"
        : "empty"
      : "loading",
  );
  const [workflowStartPending, setWorkflowStartPending] = useState(false);
  const [startedWorkflowKey, setStartedWorkflowKey] = useState<string | null>(
    null,
  );
  // 显式 Thread client 已用 epoch 隔离迟到响应；新建草稿无需销毁并重建 client，否则首条发送会落入初始化空窗。
  const workflowConversationScope = "current-conversation";
  const [selectedThreadID, setSelectedThreadID] = useState<string | null>(null);
  const selectedThreadIDRef = useRef<string | null>(null);
  const [selectionRestored, setSelectionRestored] = useState(guest);
  const [composerText, setComposerText] = useState("");
  const [optimisticComposerMessage, setOptimisticComposerMessage] =
    useState<OptimisticComposerMessage | null>(null);
  const { t, locale } = useI18n();
  const clearRegistrationSecrets = useCallback(() => {
    registrationPasswordRef.current = "";
    registrationFailureCountRef.current = 0;
    setVerificationExhausted(false);
    setVerificationEmail("");
    setVerificationCode("");
  }, []);
  const openLogin = useCallback((message = "") => {
    setLoginError(message);
    clearRegistrationSecrets();
    setAuthMode("login");
    setLoginOpen(true);
  }, [clearRegistrationSecrets]);
  // 账号菜单是轻量浮层；点击外部或按 Esc 时收起，避免遮挡聊天输入区。
  useEffect(() => {
    if (!accountMenuOpen) return;
    const closeOnOutside = (event: MouseEvent) => {
      if (!accountMenuRef.current?.contains(event.target as Node)) setAccountMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setAccountMenuOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [accountMenuOpen]);
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMobileMenuOpen(false);
      mobileMenuButtonRef.current?.focus();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mobileMenuOpen]);
  useEffect(() => () => {
    registrationPasswordRef.current = "";
  }, []);
  const handleAuthenticationRequired = useCallback(() => {
    // 账号卡片不是认证依据；任何受保护请求确认 401/403 后立即切换为访客，防止失效账号继续操作。
    setIsGuest(true);
    setContactSnapshot(undefined);
    setAccountEmail("");
    setCreditBalance(undefined);
    setCreditState("guest");
    // 清掉需要授权的目录投影，但保留当前工作流和失败气泡；重新登录后可恢复同一任务并显式重试。
    setWorkflowEntries([]);
    setWorkflowEntryState("loading");
    setNotice("");
    openLogin(t("auth.problem.expired"));
  }, [openLogin, t]);

  const loadCreditBalance = useCallback(async () => {
    if (isGuest) return;
    try {
      const response = await fetch("/api/user/credits/wallet", {cache: "no-store"});
      const payload = await response.json().catch(() => null) as {balance?: UserCreditBalance} | null;
      if (response.status === 401 || response.status === 403) {
        handleAuthenticationRequired();
        return;
      }
      const balance = payload?.balance;
      if (!response.ok || !balance || ![balance.subscription, balance.top_up, balance.reserved, balance.total, balance.capacity].every((value) => Number.isSafeInteger(value) && value >= 0)) {
        setCreditState("error");
        return;
      }
      setCreditBalance(balance);
      setCreditState("ready");
    } catch {
      setCreditState("error");
    }
  }, [handleAuthenticationRequired, isGuest]);
  const workflowConversationActive = Boolean(
    workflowConversationScope && !isGuest,
  );
  const workflowConversation = useDesignWorkflowConversation({
    active: workflowConversationActive,
    selectedThreadID,
    locale,
    conversationScope: workflowConversationScope,
    onAuthenticationRequired: handleAuthenticationRequired,
  });
  const conversationNotice = workflowConversation.notice;
  const beginConversationDraft = workflowConversation.beginDraft;
  const workflowMessages = visibleWorkflowMessages(workflowConversation.view);
  const optimisticPersistedMessage = optimisticComposerMessage && workflowConversation.view?.conversation.id === (optimisticComposerMessage.threadID || workflowConversation.view?.conversation.id)
    ? workflowMessages.find(
        (message) =>
          message.role === "user" &&
          message.sequence > optimisticComposerMessage.baselineSequence &&
          message.text.trim() === optimisticComposerMessage.text,
      )
    : undefined;
  // POST 仍在执行时先显示本地用户气泡；权威消息出现后立即由服务端时间线接管并去重。
  const activeOptimisticMessage = optimisticPersistedMessage
    ? null
    : optimisticComposerMessage;
  const displayedWorkflowMessages = activeOptimisticMessage
    ? [
        ...workflowMessages,
        {
          id: activeOptimisticMessage.id,
          sequence: activeOptimisticMessage.baselineSequence + 1,
          role: "user" as const,
          kind: "text" as const,
          text: activeOptimisticMessage.text,
          created_at: "",
        },
      ]
    : workflowMessages;
  const optimisticDisplayMessageID =
    optimisticPersistedMessage?.id ?? activeOptimisticMessage?.id;
  const optimisticFailed = optimisticComposerMessage?.phase === "failed" && !optimisticPersistedMessage;
  const workflowExecution = workflowConversation.view?.focused_execution;
  const workflowRunning = Boolean(workflowExecution && ["starting", "queued", "running", "waiting_operation", "waiting_approval", "cancelling", "waiting_gate", "stopping", "dispatching"].includes(workflowExecution.state));
  const creditExecutionMarker = workflowExecution
    ? `${workflowExecution.run_id}:${workflowExecution.run_version}:${workflowExecution.state}`
    : "";
  const pendingWorkflowProposal =
    workflowConversation.view?.pending_workflow_proposal?.state === "pending"
      ? workflowConversation.view.pending_workflow_proposal
      : undefined;
  const authoritativeAssistantAfterOptimistic = Boolean(
    optimisticPersistedMessage &&
      workflowConversation.view?.messages?.some(
        (message) =>
          message.role === "assistant" &&
          message.sequence > optimisticPersistedMessage.sequence,
      ),
  );
  const optimisticReplyCompleted = Boolean(
    optimisticComposerMessage &&
      optimisticPersistedMessage &&
      (authoritativeAssistantAfterOptimistic ||
        workflowExecution?.interaction_turns?.some(
          (turn) =>
            turn.source_message_id === optimisticPersistedMessage.id &&
            Boolean(turn.assistant_message?.trim()),
        ) || Boolean(workflowExecution?.assistant_message?.trim()) ||
        workflowExecution?.state === "succeeded" ||
        workflowExecution?.state === "failed" ||
        workflowExecution?.state === "cancelled"),
  );
  const optimisticProcessing = Boolean(
    optimisticComposerMessage?.phase === "processing" &&
      !optimisticReplyCompleted && !optimisticFailed,
  );
  const latestAssistantMessageID = [...displayedWorkflowMessages]
    .reverse()
    .find((message) => message.role === "assistant")?.id;
  const confirmationOnLatestAssistant = Boolean(
    workflowConversation.confirming &&
      !optimisticProcessing &&
      workflowExecution?.interaction_turns?.some(
        (turn) =>
          turn.interaction_version === workflowExecution.interaction_version &&
          turn.state === "waiting_confirmation" &&
          displayedWorkflowMessages.some(
            (message) =>
              message.id === latestAssistantMessageID &&
              message.role === "assistant" &&
              message.text === turn.assistant_message,
          ),
      ),
  );
  const currentAssistantRepresented = Boolean(
    workflowExecution?.assistant_message &&
      displayedWorkflowMessages.some(
        (message) =>
          message.role === "assistant" &&
          message.text.trim() === workflowExecution.assistant_message?.trim(),
      ),
  );
  const workflowTitle =
    workflowConversation.view?.conversation.title ||
    (workflowEntries.find((entry) => entry.workflowKey === startedWorkflowKey)
      ?.title[locale] ??
      startedWorkflowKey ??
      t("chat.defaultAssistantTitle"));
  const currentConversationTitle = newConversationMode
    ? t("chat.newConversation")
    : workflowTitle;
  const readOnlyNotice = workflowConversation.readOnlyReason === "conversation_closed"
    ? t("chat.readOnlyClosed")
    : workflowConversation.readOnlyReason === "task_in_progress_in_another_conversation"
      ? t("chat.readOnlyOtherTask")
      : "";
  const workflowNotice =
    workflowConversation.notice === "missing"
      ? t("chat.workflowRestoreMissing")
      : workflowConversation.notice === "restore_failed"
        ? t("chat.workflowRestoreFailed")
        : workflowConversation.notice === "changed"
          ? t("chat.workflowChangedPreserved")
          : workflowConversation.notice === "service_unavailable"
            ? t("chat.conversationServiceUnavailable")
            : workflowConversation.notice === "credits_insufficient"
              ? ""
            : "";
  const workflowStatusText =
    optimisticProcessing || workflowRunning
      ? t("chat.messageProcessing")
      : workflowConversation.confirming && !confirmationOnLatestAssistant
        ? t("chat.collectReady")
      : workflowExecution?.assistant_message ??
    (workflowConversation.loading ||
    workflowConversation.pending ||
    workflowExecution?.state === "queued" ||
    workflowExecution?.state === "running"
      ? t("chat.workflowPreparing")
      : workflowExecution?.state === "succeeded"
        ? t("chat.workflowCompleted")
        : workflowExecution?.state === "cancelled"
          ? t("chat.collectDraftEnded")
        : workflowExecution?.state === "failed"
          ? t("chat.workflowFailed")
          : t("chat.workflowWaiting"));
  const showWorkflowStatus =
    optimisticProcessing || workflowRunning ||
    (workflowConversation.confirming && !confirmationOnLatestAssistant) ||
    workflowConversation.pending ||
    workflowConversation.loading ||
    workflowExecution?.state === "queued" ||
    workflowExecution?.state === "running" ||

    (!optimisticFailed && Boolean(workflowExecution?.assistant_message) && !currentAssistantRepresented);
  // 失败气泡已经给出重试入口；底部只保留一份状态文案，避免同一错误重复出现。
  const visibleWorkflowNotice = optimisticFailed ? "" : workflowNotice;
  const assistantTyping = optimisticProcessing || workflowRunning || workflowConversation.pending || workflowStartPending;
  const workflowTimelineVersion = [
    ...displayedWorkflowMessages.map(
      (message) => `${message.id}:${message.role}:${message.text}`,
    ),
    showWorkflowStatus ? `status:${workflowStatusText}` : "",
    visibleWorkflowNotice ? `notice:${visibleWorkflowNotice}` : "",
    optimisticFailed ? "optimistic:failed" : "",
    workflowConversation.confirming ? "actions:confirming" : "",
    workflowConversation.view?.pending_workflow_proposal
      ? `proposal:${workflowConversation.view.pending_workflow_proposal.id}:${workflowConversation.view.pending_workflow_proposal.version}`
      : "",
  ].join("\u0000");
  const {
    setContainer: setConversationScrollContainer,
    onScroll: handleConversationScroll,
  } = useChatScrollFollow({
    active: !newConversationMode,
    contentVersion: workflowTimelineVersion,
  });
  const creditsExhausted = !isGuest && (creditState === "ready" && creditBalance?.total === 0 || conversationNotice === "credits_insufficient");
  const composerDisabled =
    !isGuest &&
    (creditsExhausted ||
      workflowRunning ||
      Boolean(activeOptimisticMessage?.phase === "processing") ||
      (newConversationMode
        ? workflowStartPending || workflowConversation.loading || workflowConversation.pending || workflowConversation.newConversationDisabled
        : workflowConversation.loading ||
          workflowConversation.pending ||
          !workflowConversation.writable));
  const sendDisabled = composerDisabled || (!isGuest && !composerText.trim());
  const nav: Array<[string, string, IconName]> = [
    ["chat", t("design.chat"), "chat"],
    ["contacts", contactCopy(locale).title, "user"],
    ["insights", t("design.insights"), "inbox"],
    ["saved", t("design.saved"), "star"],
    ["settings", t("design.settings"), "settings"],
  ];
  const choose = (id: string, label: string) => {
    setMobileMenuOpen(false);
    if (id === "settings") { if (!isGuest) setSettingsOpen(true); else openLogin(); setNotice(""); return; }
    setActive(id);
    if (id !== "chat" && id !== "contacts")
      setNotice(t("common.featureComingSoon", { name: label }));
    else setNotice("");
  };
  useEffect(() => {
    selectedThreadIDRef.current = selectedThreadID;
  }, [selectedThreadID]);

  useEffect(() => {
    // 存储恢复推迟到 hydration 后的任务队列，首屏仍保持服务端“新建会话”结构，避免同步级联渲染。
    const timer = window.setTimeout(() => {
      if (isGuest) {
        setSelectedThreadID(null);
        setSelectionRestored(true);
        return;
      }
      // localStorage 按规范化账号隔离，恢复值通过 UUIDv7 校验后才进入显式详情请求。
      const restored = conversationSelection.load(accountEmail, false);
      setSelectedThreadID(restored);
      setNewConversationMode(!restored);
      setSelectionRestored(true);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [accountEmail, isGuest]);

  useEffect(() => {
    const threadID = workflowConversation.view?.conversation.id;
    if (isGuest || !threadID) return;
    const timer = window.setTimeout(() => {
      setSelectedThreadID(threadID);
      conversationSelection.save(accountEmail, threadID);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [accountEmail, isGuest, workflowConversation.view?.conversation.id]);

  useEffect(() => {
    if (isGuest || !selectedThreadID || conversationNotice !== "missing") return;
    // 本地选择可能指向已被清理的历史；异步回到空白草稿，避免把用户卡在不可恢复的 404 视图。
    const timer = window.setTimeout(() => {
      conversationSelection.clear(accountEmail, isGuest);
      setSelectedThreadID(null);
      setSelectedIntent(null);
      setStartedWorkflowKey(null);
      setOptimisticComposerMessage(null);
      setNewConversationMode(true);
      beginConversationDraft();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [accountEmail, beginConversationDraft, conversationNotice, isGuest, selectedThreadID]);

  useEffect(() => {
    // 服务端已按当前认证状态提供目录时，不在 hydration 后立刻重复请求；
    // 但访客在弹窗内登录后认证状态发生变化，仍需重新读取个性化授权目录。
    if (initialWorkflowEntries && isGuest === guest) return;
    let active = true;
    void fetch("/api/user/workflow-entries", { cache: "no-store" })
      .then(async (response) => {
        const body = (await response.json().catch(() => null)) as {
          workflow_entries?: UserWorkflowEntry[];
        } | null;
        if (!active) return;
        if (!response.ok || !Array.isArray(body?.workflow_entries)) {
          if (!isGuest && (response.status === 401 || response.status === 403)) {
            handleAuthenticationRequired();
            return;
          }
          setWorkflowEntryState("error");
          return;
        }
        setWorkflowEntries(body.workflow_entries);
        setWorkflowEntryState(body.workflow_entries.length ? "ready" : "empty");
      })
      .catch(() => {
        if (active) setWorkflowEntryState("error");
      });
    return () => {
      active = false;
    };
  }, [guest, handleAuthenticationRequired, initialWorkflowEntries, isGuest]);

  useEffect(() => {
    if (isGuest) return;
    // SSR 已注入当前钱包时避免首屏重复请求；弹窗内登录后必须立即读取该账号的真实积分。
    if (initialCreditBalance && isGuest === guest) return;
    const timer = window.setTimeout(() => void loadCreditBalance(), 0);
    return () => window.clearTimeout(timer);
  }, [guest, initialCreditBalance, isGuest, loadCreditBalance]);

  useEffect(() => {
    if (isGuest || !optimisticReplyCompleted) return;
    // 对话完成后异步刷新钱包，保证本次实际结算已经写入再更新侧栏余额。
    const timer = window.setTimeout(() => void loadCreditBalance(), 0);
    return () => window.clearTimeout(timer);
  }, [isGuest, loadCreditBalance, optimisticReplyCompleted]);

  useEffect(() => {
    if (isGuest || !creditExecutionMarker) return;
    // Workflow 会在每次恢复执行时重新预占积分，等待用户时释放，成功时结算；
    // 因此不能只监听首条助手回复，必须跟随权威 Run 版本同步当前钱包。
    const immediate = window.setTimeout(() => void loadCreditBalance(), 0);
    const settled = window.setTimeout(() => void loadCreditBalance(), 1200);
    return () => {
      window.clearTimeout(immediate);
      window.clearTimeout(settled);
    };
  }, [creditExecutionMarker, isGuest, loadCreditBalance]);

  const showNewConversationDraft = () => {
    // 新会话第一阶段只清理视觉上下文并收集任务意图，不创建后端会话或消耗额度。
    setActive("chat");
    setNotice("");
    setSelectedIntent(null);
    setStartedWorkflowKey(null);
    selectedThreadIDRef.current = null;
    setSelectedThreadID(null);
    conversationSelection.clear(accountEmail, isGuest);
    workflowConversation.beginDraft();
    setComposerText("");
    setOptimisticComposerMessage(null);
    setNewConversationMode(true);
    setMobileMenuOpen(false);
  };
  const beginNewConversation = () => {
    // active_task 是全账号门禁；禁止仅靠本地清空绕过服务端正在运行的任务。
    if (workflowConversation.newConversationDisabled) return;
    showNewConversationDraft();
  };

  const deleteConversation = async (threadID: string) => {
    const archived = await workflowConversation.archive(threadID);
    // 只在当前会话确实归档后切换草稿；删历史会话或等待期间切换了会话，都不能打断当前对话。
    if (archived && selectedThreadIDRef.current === threadID) showNewConversationDraft();
  };

  /** 首页当前真实认证入口：注册先提交账号资料，再在同一弹窗消费一次性邮箱验证码。 */
  async function submitAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (loginPending) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const email = String(form.get("username") ?? "").trim();
    const password = String(form.get("password") ?? "");
    const confirmation = String(form.get("password_confirmation") ?? "");
    // 与后台一致按 Unicode 字符校验长度，不限制密码字符组合。
    if (
      authMode !== "login" &&
      (password !== confirmation ||
        Array.from(password).length < 8 ||
        Array.from(password).length > 1024)
    ) {
      setLoginError(
        password !== confirmation
          ? t("auth.passwordMismatch")
          : t("auth.passwordWeak"),
      );
      return;
    }
    setLoginPending(true);
    setLoginError("");
    try {
      const response = await fetch(
        authMode === "login" ? "/api/auth/password-login" : authMode === "recovery" ? "/api/auth/password-recovery" : "/api/auth/register",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(
            authMode === "login"
              ? { username: email, password }
              : { email, password, password_confirmation: confirmation },
          ),
        },
      );
      if (!response.ok) {
        setLoginError(
          response.status >= 500
            ? t("auth.serviceUnavailable")
            : authMode === "login"
              ? t("auth.loginInvalid")
              : t("auth.registrationInvalid"),
        );
        return;
      }
      if (authMode === "login") {
        // 失效会话入口会携带 auth_error；重新登录成功后必须移除旧错误参数，否则刷新时服务端仍按错误入口渲染访客页。
        const currentURL = new URL(window.location.href);
        currentURL.searchParams.delete("auth_error");
        currentURL.searchParams.delete("trace_id");
        window.history.replaceState(window.history.state, "", `${currentURL.pathname}${currentURL.search}${currentURL.hash}`);
        setAccountEmail(email);
        setIsGuest(false);
        setLoginOpen(false);
        setNotice(t("auth.loginSucceeded"));
      } else {
        // 注册与恢复都在验证码通过后才使用新密码；提交成功后清空表单 DOM，避免密码停留在可见节点。
        registrationPasswordRef.current = password;
        registrationFailureCountRef.current = 0;
        formElement.reset();
        setVerificationEmail(email);
        setVerificationCode("");
        setLoginError(t("auth.codeSentLong"));
      }
    } catch {
      setLoginError(t("auth.serviceUnavailable"));
    } finally {
      setLoginPending(false);
    }
  }

  async function submitVerificationCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (loginPending || !verificationEmail) return;
    const code = verificationCode.trim();
    setLoginPending(true);
    setLoginError("");
    try {
      const password = registrationPasswordRef.current;
      if (!password) {
        setLoginError(t("auth.registrationInvalid"));
        return;
      }
      const response = await fetch("/api/auth/registration-completion", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: verificationEmail, code, password, password_confirmation: password }),
      });
      if (!response.ok) {
        const problem = (await response.json().catch(() => null)) as {code?: string} | null;
        if (problem?.code === "identity.registration_auto_login_failed.v1") {
          const recoveredEmail = verificationEmail;
          clearRegistrationSecrets();
          setAuthMode("login");
          setAccountEmail(recoveredEmail);
          setLoginError(t("auth.completedLoginManually"));
          return;
        }
        registrationFailureCountRef.current += 1;
        const exhausted = problem?.code === "identity.verification_exhausted.v1";
        setVerificationExhausted(exhausted);
        setLoginError(exhausted ? t("auth.codeExhausted") : t("auth.codeInvalid"));
        return;
      }
      setAccountEmail(verificationEmail);
      setIsGuest(false);
      clearRegistrationSecrets();
      setLoginOpen(false);
      setAuthMode("login");
      setNotice(t("auth.loginSucceeded"));
    } catch {
      setLoginError(t("auth.verifyUnavailable"));
    } finally {
      setLoginPending(false);
    }
  }

  async function executeWorkflowStart(message: OptimisticComposerMessage) {
    setWorkflowStartPending(true);
    setOptimisticComposerMessage({ ...message, phase: "processing" });
    setNotice("");
    try {
      // 同一失败气泡重试必须复用幂等键，避免首个响应丢失后创建第二个 Run。
      const response = await fetch("/api/user/workflow-starts", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-softmap-locale": locale,
          "idempotency-key": message.idempotencyKey,
        },
        body: JSON.stringify({
          workflow_key: message.workflowKey,
          message: message.text,
        }),
      });
      const payload = (await response.json().catch(() => null)) as {
        code?: string;
        trace_id?: string;
        result?: MessageReceipt;
      } | null;
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleAuthenticationRequired();
        } else if (payload?.code === "conversation.route_unavailable.v1") {
          setNotice(t("chat.workflowStartUnavailable"));
        } else if (response.status === 409) {
          setNotice(t("chat.workflowConflict"));
        } else {
          const unavailable = t("chat.workflowServiceUnavailable");
          setNotice(
            payload?.trace_id
              ? `${unavailable} ${t("common.traceID", { traceID: payload.trace_id })}`
              : unavailable,
          );
        }
        setOptimisticComposerMessage({ ...message, phase: "failed" });
        return;
      }
      const threadID = payload?.result?.conversation?.id;
      if (!threadID) throw new Error("workflow start did not return a thread");
      // 首条成功回执直接绑定 Thread；禁止再通过 /current 猜测新建结果。
      setSelectedThreadID(threadID);
      conversationSelection.save(accountEmail, threadID);
      // 启动回执已包含首条消息和执行事实，直接写入当前会话，避免成功后再次 GET 造成重复等待。
      workflowConversation.hydrateReceipt(payload.result!);
      setNotice("");
    } catch {
      setOptimisticComposerMessage({ ...message, phase: "failed" });
      setNotice(t("chat.workflowServiceUnavailable"));
    } finally {
      setWorkflowStartPending(false);
    }
  }

  async function executeWorkflowMessage(message: OptimisticComposerMessage) {
    setOptimisticComposerMessage({ ...message, phase: "processing" });
    setNotice("");
    const succeeded = await workflowConversation.send(message.text);
    if (!succeeded) {
      // 控制器在同步竞态中可能拒绝发送；只有同一 Thread 的新消息未落库时才提示重试。
      const accepted = workflowConversation.view?.conversation.id === message.threadID && workflowConversation.view?.messages?.some(
        (entry) => entry.role === "user" && entry.sequence > message.baselineSequence && entry.text.trim() === message.text,
      );
      if (!accepted) setOptimisticComposerMessage({ ...message, phase: "failed" });
    }
  }

  function retryOptimisticMessage() {
    const message = optimisticComposerMessage;
    if (!message || message.phase !== "failed") return;
    if (isGuest) {
      // 访客主动发送只需要登录门禁，不应误导为已有会话过期。
      openLogin();
      return;
    }
    // 零余额时旧失败气泡也不能绕过发送门禁；充值到账后钱包刷新会自然恢复重试能力。
    if (creditsExhausted) return;
    if (message.operation === "start") {
      void executeWorkflowStart(message);
    } else {
      void executeWorkflowMessage(message);
    }
  }

  async function submitComposer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isGuest) {
      // 访客可以保留输入，但不能让受保护请求越过登录边界。
      openLogin();
      return;
    }
    // UI 禁用之外再做提交入口检查，覆盖 Enter 和脚本触发，确保零余额消息不会进入时间线或请求队列。
    if (creditsExhausted) return;
    if (!newConversationMode) {
      if (
        workflowRunning ||
        workflowConversation.loading ||
        !workflowConversation.writable ||
        workflowConversation.pending ||
        activeOptimisticMessage?.phase === "processing"
      )
        return;
      const message = composerText.trim();
      if (!message) return;
      const optimistic: OptimisticComposerMessage = {
        id: `optimistic-${crypto.randomUUID()}`,
        text: message,
        phase: "processing",
        operation: "send",
        workflowKey: startedWorkflowKey ?? "",
        threadID: workflowConversation.view?.conversation.id,
        idempotencyKey: "",
        baselineSequence: workflowMessages.reduce(
          (maximum, item) => Math.max(maximum, item.sequence),
          0,
        ),
      };
      // 像即时聊天一样先落到对话时间线，输入框不承担网络状态展示。
      setComposerText("");
      setOptimisticComposerMessage(optimistic);
      void executeWorkflowMessage(optimistic);
      return;
    }
    if (workflowStartPending || workflowConversation.loading || workflowConversation.pending || workflowConversation.newConversationDisabled || activeOptimisticMessage?.phase === "processing") return;
    const initialMessage = composerText.trim();
    if (!initialMessage) return;
    const optimistic: OptimisticComposerMessage = {
      id: `optimistic-${crypto.randomUUID()}`,
      text: initialMessage,
      phase: "processing",
      operation: selectedIntent ? "start" : "send",
      workflowKey: selectedIntent ?? "",
      idempotencyKey: selectedIntent ? `workflow-start-${crypto.randomUUID()}` : "",
      baselineSequence: selectedIntent
        ? 0
        : workflowMessages.reduce(
            (maximum, item) => Math.max(maximum, item.sequence),
            0,
          ),
    };
    // 未选择卡片时进入通用助手；显式卡片仍保留原子 workflow-starts 快捷链路。
    setComposerText("");
    setStartedWorkflowKey(selectedIntent);
    setNewConversationMode(false);
    setOptimisticComposerMessage(optimistic);
    if (selectedIntent) void executeWorkflowStart(optimistic);
    else void executeWorkflowMessage(optimistic);
  }
  // 验证码输入可能持续数分钟；遮罩只承担视觉隔离，关闭必须由显式按钮触发，避免误点丢失进度。
  const openMailContacts=useCallback((target?:MailNotificationTarget)=>{setActive("contacts");setMobileMenuOpen(false);if(target)setMailTarget({account:accountEmail,target:{...target,request_id:crypto.randomUUID()}});},[accountEmail]);
  return (
    <main className="design-page">
      <svg className="design-icon-sprite" aria-hidden="true">
        <symbol id="design-i-chat" viewBox="0 0 24 24">
          <path d="M20 11.5a7.5 7.5 0 0 1-8 7.5 8.7 8.7 0 0 1-4.1-1L4 20l1.5-3.2A7.3 7.3 0 0 1 4.5 12 7.5 7.5 0 0 1 12 4.5a7.5 7.5 0 0 1 8 7Z" />
          <path d="M8 12h.01M12 12h.01M16 12h.01" />
        </symbol>
        <symbol id="design-i-inbox" viewBox="0 0 24 24">
          <path d="M4 5h16v14H4z" />
          <path d="M4 14h4l2 2h4l2-2h4" />
        </symbol>
        <symbol id="design-i-star" viewBox="0 0 24 24">
          <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9L12 3Z" />
        </symbol>
        <symbol id="design-i-settings" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-1.8 1.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V20h-2.6v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1-1.8-1.8.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H6v-2.6h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1L9 6.6l.1.1a1.7 1.7 0 0 0 1.9.3 1.7 1.7 0 0 0 1-1.5V5h2.6v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1 1.8 1.8-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.5 1h.1v2.6h-.1a1.7 1.7 0 0 0-1.5 1Z" />
        </symbol>
        <symbol id="design-i-bot" viewBox="0 0 24 24">
          <rect x="4" y="7" width="16" height="12" rx="4" />
          <path d="M12 3v4M8 12h.01M16 12h.01M8 16c1.5 1 6.5 1 8 0" />
        </symbol>
        <symbol id="design-i-user" viewBox="0 0 24 24">
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5 20a7 7 0 0 1 14 0" />
        </symbol>
        <symbol id="design-i-paperclip" viewBox="0 0 24 24">
          <path d="m9 17 7.6-7.6a3 3 0 0 0-4.2-4.2L4.8 12.8a4.5 4.5 0 0 0 6.4 6.4l7.2-7.2" />
        </symbol>
        <symbol id="design-i-send" viewBox="0 0 24 24">
          <path d="m4 4 16 8-16 8 3-8-3-8Z" />
          <path d="M7 12h13" />
        </symbol>
        <symbol id="design-i-chevron" viewBox="0 0 24 24">
          <path d="m9 5 7 7-7 7" />
        </symbol>
      </svg>
      <span className="design-compatibility-copy">
        {t("design.compatibility", { text: compatibilityText })}
      </span>
      <div
        className="design-compatibility-dialog"
        role="dialog"
        aria-hidden="true"
      >
        {compatibilityText}
      </div>
      <div className="app-shell">
        <aside className={`sidebar ${mobileMenuOpen ? "is-mobile-open" : ""}`}>
          {mobileMenuOpen ? <button className="mobile-menu-close" type="button" aria-label={t("design.closeMenu")} onClick={() => { setMobileMenuOpen(false); mobileMenuButtonRef.current?.focus(); }}>×</button> : null}
          <div className="brand">
            <div className="brand-name">SoftMap AI</div>
          </div>
          <button
            className="new-chat"
            type="button"
            onClick={beginNewConversation}
            disabled={workflowConversation.newConversationDisabled}
            aria-describedby={workflowConversation.newConversationDisabled ? "new-conversation-disabled-reason" : undefined}
          >
            <span className="plus">+</span>
            <span>{t("design.newChat")}</span>
            <span className="chevron">›</span>
          </button>
          {workflowConversation.newConversationDisabled ? <p id="new-conversation-disabled-reason" className="new-chat-disabled-reason">
            {t("chat.newDisabledActiveTask", {name: workflowConversation.activeTask?.display_name || workflowConversation.activeTask?.thread_title || t("design.chat")})}
          </p> : null}
          <nav className="nav-list" aria-label={t("design.primaryNav")}>
            {nav.map(([id, label, icon]) => (
              <button
                key={id}
                className={`nav-item ${active === id ? "active" : ""}`}
                type="button"
                aria-current={active === id ? "page" : undefined}
                onClick={() => choose(id, label)}
              >
                <Icon name={icon} />
                <span className="nav-label">{label}</span>
                {id==="contacts"&&mailUnread&&mailUnread.total>0&&<span className="mail-unread-badge" aria-label={unreadLabel(mailUnread.total,locale)}>{mailUnread.total>99?"99+":mailUnread.total}</span>}
                {id !== "chat" && id !== "contacts" && id !== "settings" ? <small>{t("common.comingSoon")}</small> : null}
              </button>
            ))}
          </nav>
          <div className="sidebar-spacer" />
          <section
            className="account-card"
            aria-label={t("design.accountCredits")}
          >
            <div className="credit-card">
              <div className="credit-title">
                {t("design.aiCredits")} <span>ⓘ</span>
              </div>
              <div className="credit-value">
                {isGuest
                  ? t("design.loginToView")
                  : creditState === "ready" && creditBalance
                    ? `${new Intl.NumberFormat(locale).format(creditBalance.total)} / ${new Intl.NumberFormat(locale).format(creditBalance.capacity)}`
                    : creditState === "error"
                      ? t("design.creditsUnavailable")
                      : t("design.creditsLoading")}
              </div>
              <div
                className="progress"
                role="progressbar"
                aria-label={t("design.creditRemaining")}
                aria-valuemin={0}
                aria-valuemax={creditBalance?.capacity ?? 0}
                aria-valuenow={creditBalance?.total ?? 0}
              >
                <span style={{width: `${creditBalance?.capacity ? Math.min(100, creditBalance.total / creditBalance.capacity * 100) : 0}%`}} />
              </div>
            </div>
            <div className="account-profile-menu" ref={accountMenuRef}>
              <button
              ref={accountButtonRef}
              className={`profile ${accountMenuOpen ? "is-open" : ""}`}
              type="button"
              aria-expanded={!isGuest ? accountMenuOpen : undefined}
              onClick={
                isGuest ? () => openLogin() : () => setAccountMenuOpen((open) => !open)
              }
              onPointerDown={
                isGuest
                  ? (event) => {
                      event.preventDefault();
                      openLogin();
                    }
                  : undefined
              }
            >
              <span className="avatar">
                <Icon name="user" />
              </span>
              <span>
                {isGuest ? (
                  <>
                    <span className="profile-name">{t("auth.login")}</span>
                    <span className="profile-org">
                      {t("design.syncConversation")}
                    </span>
                  </>
                ) : (
                  <span className="profile-name">{accountEmail || "—"}</span>
                )}
              </span>
              <Icon name="chevron" className="chevron" />
              </button>
              {!isGuest && accountMenuOpen ? (
                <div className="account-profile-popover" role="menu">
                  <button type="button" role="menuitem" onClick={() => { accountButtonRef.current?.focus(); setAccountMenuOpen(false); setSettingsOpen(true); }}>{t("design.settings")}</button>
                  <form action="/api/auth/logout" method="post"><button type="submit" role="menuitem">{t("nav.logout")}</button></form>
                </div>
              ) : null}
            </div>
            {!isGuest ? (
              <Link className="upgrade" href="/user/credits">
                {t("design.upgrade")}
              </Link>
            ) : null}
          </section>
        </aside>
        <section className="workspace">
          <header className="topbar">
            <button ref={mobileMenuButtonRef} className="mobile-menu-trigger" type="button" aria-label={t("design.openMenu")} aria-expanded={mobileMenuOpen} onClick={() => setMobileMenuOpen(true)}><span /><span /><span /></button>
            {active === "contacts" ? <strong>{contactCopy(locale).title}</strong> : <ConversationHistoryMenu
              title={currentConversationTitle}
              conversations={workflowConversation.list}
              selectedThreadID={selectedThreadID}
              loading={!selectionRestored || workflowConversation.listLoading}
              error={workflowConversation.notice === "restore_failed" && workflowConversation.list.length === 0}
              onRetry={() => void workflowConversation.refresh()}
              onDelete={(threadID) => void deleteConversation(threadID)}
              onSelect={(threadID) => {
                setSelectedIntent(null);
                setStartedWorkflowKey(null);
                setOptimisticComposerMessage(null);
                selectedThreadIDRef.current = threadID;
                setSelectedThreadID(threadID);
                conversationSelection.save(accountEmail, threadID, isGuest);
                setNewConversationMode(false);
                void workflowConversation.select(threadID);
              }}
            />}
            <div className="top-actions">
              <MailNotificationCenter key={accountEmail} account={accountEmail} enabled={!isGuest} ready={active!=="contacts"||contactsLoadedFor===accountEmail} locale={locale} onOpen={openMailContacts}/>
              <LanguageSwitcher />
            </div>
          </header>
          {active === "contacts" ? <ContactsWorkspace key={isGuest?"guest":accountEmail} locale={locale} guest={isGuest} mailUnread={mailUnread} mailTarget={mailTarget?.account===accountEmail?mailTarget.target:undefined} onContactsLoaded={onContactsLoaded} initialSnapshot={!isGuest&&contactSnapshot?.account===accountEmail?contactSnapshot.value:undefined} onSnapshot={cacheContacts} settingsOpen={settingsOpen} onLogin={()=>openLogin()} /> : <><section
            className={`conversation ${newConversationMode ? "is-new-conversation" : ""}`}
            aria-label={t("design.conversation")}
            ref={setConversationScrollContainer}
            onScroll={handleConversationScroll}
          >
            {newConversationMode ? (
              <NewConversationChooser
                entries={workflowEntries}
                state={workflowEntryState}
                selected={selectedIntent}
                onSelect={setSelectedIntent}
              />
            ) : (
              <section
                className="design-workflow-thread"
                aria-label={workflowTitle}
                aria-live="polite"
              >
                <header className="design-workflow-heading">
                  <span>{t("design.chat")}</span>
                  <h2>{workflowTitle}</h2>
                </header>
                {displayedWorkflowMessages.map((message) =>
                  message.role === "user" ? (
                    <article className="message-row user" key={message.id}>
                      <div className="bubble user-bubble">
                        <p>{message.text}</p>
                        {optimisticFailed &&
                        message.id === optimisticDisplayMessageID ? (
                          <button
                            className="design-message-retry"
                            type="button"
                            onClick={retryOptimisticMessage}
                            disabled={
                              workflowStartPending ||
                              workflowConversation.loading ||
                              workflowConversation.pending
                            }
                          >
                            {t("chat.messageRetry")}
                          </button>
                        ) : null}
                      </div>
                      <span className="user-avatar">
                        <Icon name="user" />
                      </span>
                    </article>
                  ) : (
                    <article className={`message-row ${isGoogleMapsPresentation(message.presentation) ? "has-map-result" : ""}`} key={message.id}>
                      <span className="bot-avatar">
                        <Icon name="bot" />
                      </span>
                      <div className={`bubble ${isGoogleMapsPresentation(message.presentation) ? "map-result-bubble" : ""}`}>
                        <p>{isGoogleMapsPresentation(message.presentation) ? googleMapsResultSummary(message.presentation, locale) : message.text}</p>
                        <GoogleMapsResultPresentation value={message.presentation} locale={locale} threadID={workflowConversation.view?.conversation.id} />
                        {pendingWorkflowProposal &&
                        message.id === latestAssistantMessageID ? (
                          <div className="design-workflow-actions">
                            <button
                              type="button"
                              onClick={() => void workflowConversation.acceptWorkflowProposal()}
                              disabled={workflowConversation.pending}
                            >
                              {pendingWorkflowProposal.continuation ? t("chat.continueSearch") : t("chat.acceptWorkflowProposal", {name: pendingWorkflowProposal.display_name})}
                            </button>
                            <button
                              type="button"
                              onClick={() => void workflowConversation.rejectWorkflowProposal()}
                              disabled={workflowConversation.pending}
                            >
                              {pendingWorkflowProposal.continuation ? t("chat.resetSearch") : t("chat.rejectWorkflowProposal")}
                            </button>
                          </div>
                        ) : null}
                        {confirmationOnLatestAssistant &&
                        message.id === latestAssistantMessageID ? (
                          <div className="design-workflow-actions">
                            <button
                              type="button"
                              onClick={() => void workflowConversation.confirm()}
                              disabled={workflowConversation.pending}
                            >
                              {t("chat.confirmContinue")}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </article>
                  ),
                )}
                {showWorkflowStatus ? (
                <article className="message-row design-workflow-status">
                  <span className="bot-avatar">
                    <Icon name="bot" />
                  </span>
                  <div className="bubble">
                    <strong>SoftMap AI</strong>
                    {assistantTyping ? <div className="assistant-typing" role="status" aria-label={t("chat.messageProcessing")}>
                      <span className="assistant-typing-dots" aria-hidden="true"><i /><i /><i /></span>
                      <span>{t("chat.messageProcessing")}</span>
                    </div> : <p>{workflowStatusText}</p>}
                    {workflowConversation.confirming && !optimisticProcessing && !confirmationOnLatestAssistant ? (
                      <div className="design-workflow-actions">
                        <button
                          type="button"
                          onClick={() => void workflowConversation.confirm()}
                          disabled={workflowConversation.pending}
                        >
                          {t("chat.confirmContinue")}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </article>
                ) : null}
                {readOnlyNotice ? <div className="design-workflow-notice design-workflow-read-only" role="status">{readOnlyNotice}</div> : null}
                {visibleWorkflowNotice ? (
                  <div className="design-workflow-notice" role="status">
                    <span>{visibleWorkflowNotice}</span>
                    <button
                      type="button"
                      onClick={() => void workflowConversation.refresh()}
                      disabled={workflowConversation.loading}
                    >
                      {t("common.retry")}
                    </button>
                  </div>
                ) : null}
              </section>
            )}
          </section>
          <div className="composer-wrap">
            {creditsExhausted ? (
              <div className="credit-empty-notice" role="alert">
                <span><strong>{t("chat.creditsEmptyTitle")}</strong>{t("chat.creditsEmptyCopy")}</span>
                <Link href="/user/credits">{t("chat.creditsTopUp")}</Link>
              </div>
            ) : null}
            <form className="composer" onSubmit={submitComposer}>
              <div className="composer-main">
                <input
                  name="message"
                  aria-label={t("design.inputLabel")}
                  // 浏览器扩展可能在 React 接管前为可输入元素注入 data-has-listeners；仅忽略该元素的属性水合差异。
                  suppressHydrationWarning
                  value={composerText}
                  onChange={(event) =>
                    setComposerText(event.currentTarget.value)
                  }
                  placeholder={
                    creditsExhausted
                      ? t("chat.creditsEmptyPlaceholder")
                      : newConversationMode && !selectedIntent
                      ? t("chat.selectIntentPlaceholder")
                      : t("design.inputPlaceholder")
                  }
                  disabled={!isGuest && (workflowConversation.readOnly || creditsExhausted || workflowRunning || workflowStartPending || workflowConversation.loading || workflowConversation.pending)}
                />
                <div className="composer-tools">
                  <button
                    className="tool-btn"
                    type="button"
                    onClick={() => setNotice(t("design.attachmentSoon"))}
                    disabled={composerDisabled}
                  >
                    <Icon name="paperclip" className="icon-sm" />
                    {t("design.attachment")}
                  </button>
                </div>
              </div>
              <div className="composer-actions">
                <button
                  className="send-btn"
                  type="submit"
                  aria-label={t("design.send")}
                  disabled={sendDisabled}
                >
                  <Icon name="send" />
                </button>
              </div>
            </form>
            <p className="disclaimer">
              {notice || readOnlyNotice || t("design.disclaimer") }
            </p>
          </div></>}
        </section>
      </div>
      {settingsOpen && !isGuest ? <UserSettingsDialog locale={locale} onClose={() => setSettingsOpen(false)} onChangePassword={() => { setSettingsOpen(false); clearRegistrationSecrets(); setAuthMode("recovery"); setLoginError(""); setLoginOpen(true); }} /> : null}
      {mobileMenuOpen ? <button className="mobile-menu-scrim" type="button" aria-label={t("design.closeMenu")} onClick={() => { setMobileMenuOpen(false); mobileMenuButtonRef.current?.focus(); }} /> : null}
      {loginOpen ? (
        <div className="design-login-backdrop" role="presentation">
          <div
            className="design-login-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="design-login-title"
          >
            <button
              className="design-login-close"
              type="button"
              aria-label={t("design.closeLogin")}
              disabled={loginPending}
              onClick={() => {
                clearRegistrationSecrets();
                setLoginOpen(false);
              }}
            >
              ×
            </button>
            <p className="design-login-eyebrow">SoftMap AI</p>
            <h2 id="design-login-title">
              {authMode === "login" ? t("auth.loginAccount") : authMode === "recovery" ? t("auth.recoverAccount") : t("auth.registerAccount")}
            </h2>
            <p className="design-login-copy">
              {verificationEmail ? t("auth.verificationCopy") : authMode === "login" ? t("auth.loginCopy") : authMode === "recovery" ? t("auth.recoveryCopy") : t("auth.registerCopy")}
            </p>
            {/* 两个表单语义不同，必须隔离节点生命周期，避免非受控输入切换为受控输入。 */}
            {verificationEmail ? (
              <form
                key="verification"
                autoComplete="off"
                onSubmit={submitVerificationCode}
              >
                <label htmlFor="design-registration-code">
                  {t("auth.verificationCode")}
                  <input
                    id="design-registration-code"
                    name="verification_code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={verificationCode}
                    onChange={(event) =>
                      setVerificationCode(
                        event.target.value.replace(/\D/g, "").slice(0, 6),
                      )
                    }
                    required
                    minLength={6}
                    maxLength={6}
                    pattern="[0-9]{6}"
                  />
                </label>
                {loginError ? (
                  <p className="design-login-error" role="alert">
                    {loginError}
                  </p>
                ) : null}
                <button
                  className="design-login-submit"
                  type="submit"
                  disabled={loginPending || verificationExhausted || verificationCode.length !== 6}
                >
                  {loginPending
                    ? t("auth.verifying")
                    : t("auth.verifyComplete")}
                </button>
              </form>
            ) : (
              <form key="account" onSubmit={submitAccount}>
                <label htmlFor="design-login-username">
                  {t("auth.email")}
                  <input
                    id="design-login-username"
                    name="username"
                    type="email"
                    required
                    autoComplete="email"
                    defaultValue={authMode === "login" || authMode === "recovery" ? accountEmail : ""}
                  />
                </label>
                <label htmlFor="design-login-password">
                  {authMode === "login"
                    ? t("auth.password")
                    : t("auth.setPassword")}
                  <input
                    id="design-login-password"
                    name="password"
                    type="password"
                    required
                    minLength={authMode !== "login" ? 8 : undefined}
                    autoComplete={
                      authMode === "login" ? "current-password" : "new-password"
                    }
                  />
                </label>
                {authMode !== "login" ? (
                  <>
                    <label htmlFor="design-login-password-confirmation">
                      {t("auth.confirmPassword")}
                      <input
                        id="design-login-password-confirmation"
                        name="password_confirmation"
                        type="password"
                        required
                        minLength={8}
                        autoComplete="new-password"
                      />
                    </label>
                    <small>{t("auth.passwordRule")} {t("auth.existingAccountPasswordNotice")}</small>
                  </>
                ) : null}
                {loginError ? (
                  <p className="design-login-error" role="alert">
                    {loginError}
                  </p>
                ) : null}
                <button
                  className="design-login-submit"
                  type="submit"
                  disabled={loginPending}
                >
                  {loginPending
                    ? t("auth.submitting")
                    : authMode === "login"
                      ? t("auth.login")
                      : authMode === "recovery" ? t("auth.recoverAccount") : t("auth.register")}
                </button>
              </form>
            )}
            {verificationEmail ? null : (
              <div className="design-login-links">
                <button
                  type="button"
                  onClick={() => {
                    clearRegistrationSecrets();
                    setAuthMode("recovery");
                    setLoginError("");
                  }}
                >
                  {t("auth.forgotPassword")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    clearRegistrationSecrets();
                    setAuthMode(authMode === "login" ? "register" : "login");
                    setLoginError("");
                  }}
                >
                  {authMode === "login"
                    ? t("auth.registerAccount")
                    : t("auth.backLogin")}
                </button>
              </div>
            )}
            <div className="design-legal-links">
              <a href="https://www.softmap.ai/privacy" target="_blank" rel="noopener noreferrer">{locale === "en" ? "Privacy Policy" : locale === "zh-TW" ? "隱私政策" : "隐私政策"}</a>
              <a href="https://www.softmap.ai/terms" target="_blank" rel="noopener noreferrer">{locale === "en" ? "Terms of Service" : locale === "zh-TW" ? "服務條款" : "服务条款"}</a>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
